// @ts-check

import { createHint } from "../engine/hint-engine.js";
import {
  applyHint,
  conflictCells,
  createGame,
  erase,
  isComplete,
  pauseGame,
  placeDigit,
  publicUnitCompletions,
  redo,
  revealCell,
  selectCell,
  tickGame,
  undo,
} from "../store/game-state.js";
import { cellIdToIndex, validateActionShape } from "./capabilities.js";

export const AUTHORITATIVE_COMMANDS = Object.freeze([
  "start_puzzle",
  "place_digit",
  "toggle_candidate_note",
  "erase_cell",
  "undo",
  "redo",
  "request_hint",
  "advance_hint",
  "dismiss_hint",
  "apply_hint",
  "reveal_cell",
  "pause_game",
  "resume_game",
  "set_gameplay_options",
  "advance_time",
  "select_cell",
  "set_note_mode",
]);

export class GameCommandService {
  constructor(options) {
    this.store = options.store;
    this.catalog = options.catalog;
    this.director = options.director ?? null;
    this.now = options.now ?? (() => new Date());
    this.onComplete = options.onComplete ?? (() => {});
    this.onEvents = options.onEvents ?? (() => {});
    this.onCommit = options.onCommit ?? (() => {});
    this.acceptedSequence = Number(options.acceptedSequence ?? 0);
    this.revision = Number(options.revision ?? 0);
    this.events = [...(options.events ?? [])].slice(-200);
  }

  get state() {
    return this.store.getState();
  }
  puzzle(id = this.state.activeGame?.puzzle_id) {
    return this.catalog.puzzles.find((item) => item.id === id) ?? null;
  }

  validate(action, exposure = "agent") {
    const shape = validateActionShape(action, exposure);
    if (!shape.ok) return shape;
    return this.domainValidation(action);
  }

  execute(action, options = {}) {
    const exposure = options.exposure ?? "agent";
    const checked = this.validate(action, exposure);
    if (!checked.ok) return checked;
    const before = this.state.activeGame;
    const beforeConflicts = before ? conflictCells(before).size : 0;
    let game = before;
    let resultEvents = [];
    const cell = "cell" in action ? cellIdToIndex(action.cell) : -1;
    switch (action.type) {
      case "start_puzzle": {
        const puzzle = this.puzzle(action.puzzle_id);
        const same = before?.puzzle_id === puzzle.id && !before.completed_at;
        game = same
          ? before
          : createGame(puzzle, {
              dailyDate: action.daily_date ?? null,
              now: this.now,
            });
        if (!same) this.director?.reset();
        this.store.dispatch({ type: "START_GAME", game });
        resultEvents.push(
          event(
            "important_result",
            same ? "puzzle_resumed" : "puzzle_started",
            {
              puzzle_id: puzzle.id,
              daily_date: action.daily_date ?? null,
            },
          ),
        );
        break;
      }
      case "place_digit": {
        const noteMode = game.note_mode;
        game = placeDigit(
          { ...game, selected_cell: cell, note_mode: false },
          this.puzzle(),
          action.digit,
          this.state.settings,
          this.now,
        );
        game = { ...game, note_mode: noteMode, selected_cell: cell };
        this.updateGame(game);
        const afterConflicts = conflictCells(game).size;
        const mistakeRecorded = game.mistakes > before.mistakes;
        resultEvents.push(
          event(
            mistakeRecorded || afterConflicts > beforeConflicts
              ? "critical_event"
              : "important_result",
            mistakeRecorded
              ? "mistake_recorded"
              : afterConflicts > beforeConflicts
                ? "conflict_introduced"
                : beforeConflicts > 0 && afterConflicts === 0
                  ? "correction"
                  : "digit_entered",
            { cell: action.cell, digit: action.digit },
          ),
        );
        break;
      }
      case "toggle_candidate_note": {
        const noteMode = game.note_mode;
        game = placeDigit(
          { ...game, selected_cell: cell, note_mode: true },
          this.puzzle(),
          action.digit,
          this.state.settings,
          this.now,
        );
        game = { ...game, note_mode: noteMode, selected_cell: cell };
        this.updateGame(game);
        resultEvents.push(
          event("important_result", "candidate_note_toggled", {
            cell: action.cell,
            digit: action.digit,
          }),
        );
        break;
      }
      case "erase_cell":
        game = erase({ ...game, selected_cell: cell }, this.puzzle());
        game = { ...game, selected_cell: cell };
        this.updateGame(game);
        resultEvents.push(
          event("important_result", "cell_erased", { cell: action.cell }),
        );
        break;
      case "undo":
        game = undo(game);
        this.updateGame(game);
        resultEvents.push(event("important_result", "undo"));
        break;
      case "redo":
        game = redo(game);
        this.updateGame(game);
        resultEvents.push(event("important_result", "redo"));
        break;
      case "request_hint": {
        const hint = createHint(game.values);
        if (!hint)
          return domainFailure(
            "NO_HINT_AVAILABLE",
            "No supported logical hint is available.",
            {},
            ["board"],
            ["reveal_cell"],
          );
        game = { ...game, hint, hint_stage: 0 };
        this.updateGame(game);
        resultEvents.push(
          event("important_result", "hint_opened", {
            technique: hint.technique,
          }),
        );
        break;
      }
      case "advance_hint":
        game = {
          ...game,
          hint_stage: Math.min(
            game.hint.stages.length - 1,
            game.hint_stage + 1,
          ),
        };
        this.updateGame(game);
        resultEvents.push(
          event("important_result", "hint_advanced", {
            stage: game.hint_stage + 1,
          }),
        );
        break;
      case "dismiss_hint":
        game = { ...game, hint: null, hint_stage: 0 };
        this.updateGame(game);
        resultEvents.push(event("important_result", "hint_dismissed"));
        break;
      case "apply_hint":
        game = applyHint(game, this.puzzle(), game.hint, this.now);
        this.updateGame(game);
        resultEvents.push(event("important_result", "hint_applied"));
        break;
      case "reveal_cell":
        game = {
          ...revealCell(
            { ...game, selected_cell: cell },
            this.puzzle(),
            this.now,
          ),
          selected_cell: cell,
          hint: null,
          hint_stage: 0,
        };
        this.updateGame(game);
        resultEvents.push(
          event("important_result", "cell_revealed", {
            cell: action.cell,
            digit: Number(game.values[cell]),
          }),
        );
        break;
      case "pause_game":
        game = pauseGame(game, true);
        this.updateGame(game);
        resultEvents.push(event("important_result", "game_paused"));
        break;
      case "resume_game":
        game = pauseGame(game, false);
        this.updateGame(game);
        resultEvents.push(event("important_result", "game_resumed"));
        break;
      case "set_gameplay_options": {
        const settings = {};
        if ("check_against_solution" in action)
          settings.checkAgainstSolution = action.check_against_solution;
        if ("auto_remove_peer_notes" in action)
          settings.autoRemovePeerNotes = action.auto_remove_peer_notes;
        this.store.dispatch({ type: "SETTINGS", settings });
        resultEvents.push(
          event("important_result", "gameplay_options_changed"),
        );
        break;
      }
      case "advance_time":
        game = tickGame(game, action.milliseconds);
        this.updateGame(game);
        break;
      case "select_cell":
        game = selectCell(game, cell);
        this.updateGame(game);
        break;
      case "set_note_mode":
        game =
          game.note_mode === action.enabled
            ? game
            : {
                ...game,
                note_mode: action.enabled,
                action_count: game.action_count + 1,
              };
        this.updateGame(game);
        resultEvents.push(
          event("important_result", action.enabled ? "notes_on" : "notes_off"),
        );
        break;
      default:
        return domainFailure(
          "UNSUPPORTED_COMMAND",
          `No authoritative handler exists for ${action.type}.`,
        );
    }

    if (
      game &&
      ["place_digit", "redo", "apply_hint", "reveal_cell"].includes(action.type)
    ) {
      const progress = this.applyPublicProgress(game);
      game = progress.game;
      resultEvents.push(...progress.events);
      const completion = this.completeIfNeeded(game);
      resultEvents.push(...completion.events);
    }

    this.acceptedSequence += 1;
    this.revision += 1;
    resultEvents.push(...this.companionEvents(resultEvents));
    const receipt = {
      id: `receipt_${String(this.acceptedSequence).padStart(6, "0")}`,
      action_type: action.type,
      accepted: true,
      revision: this.revision,
      changes: summarizeChanges(before, this.state.activeGame),
    };
    this.recordEvents(resultEvents);
    this.onCommit(receipt, action);
    return {
      ok: true,
      receipt,
      events: structuredClone(resultEvents),
      revision: this.revision,
    };
  }

  domainValidation(action) {
    if (action.type === "start_puzzle") {
      const puzzle = this.puzzle(action.puzzle_id);
      if (!puzzle)
        return domainFailure(
          "UNKNOWN_PUZZLE",
          "The requested puzzle is not published.",
          { puzzle_id: action.puzzle_id },
          ["catalog"],
        );
      if (
        action.daily_date &&
        this.catalog.daily.entries[action.daily_date]?.puzzle_id !==
          action.puzzle_id
      )
        return domainFailure(
          "INVALID_DAILY_PUZZLE",
          "The date does not publish this puzzle.",
          { daily_date: action.daily_date, puzzle_id: action.puzzle_id },
          ["catalog"],
        );
      return { ok: true };
    }
    if (
      [
        "navigate",
        "set_presentation_preferences",
        "open_dialog",
        "close_dialog",
        "share_result",
        "reset_statistics",
        "set_gameplay_options",
      ].includes(action.type)
    )
      return { ok: true };
    const game = this.state.activeGame;
    if (!game)
      return domainFailure(
        "NO_ACTIVE_GAME",
        "Start a puzzle before using this action.",
        {},
        ["catalog"],
        ["start_puzzle"],
      );
    if (
      game.completed_at &&
      !["start_puzzle", "select_cell"].includes(action.type)
    )
      return domainFailure(
        "GAME_COMPLETED",
        "The active puzzle is already complete.",
        { puzzle_id: game.puzzle_id },
        ["summary"],
        ["start_puzzle"],
      );
    if (
      game.paused &&
      !["resume_game", "pause_game", "advance_time", "select_cell"].includes(
        action.type,
      )
    )
      return domainFailure(
        "GAME_PAUSED",
        "Resume the puzzle before changing it.",
        {},
        ["summary"],
        ["resume_game"],
      );
    if ("cell" in action && action.type !== "select_cell") {
      const cell = cellIdToIndex(action.cell);
      const puzzle = this.puzzle();
      if (puzzle.givens[cell] !== "0")
        return domainFailure(
          "CELL_IS_GIVEN",
          "Given cells cannot be changed.",
          { cell: action.cell },
          ["board"],
          ["find_candidates"],
        );
    }
    if (action.type === "undo" && !game.history.length)
      return domainFailure(
        "NOTHING_TO_UNDO",
        "There is no board edit to undo.",
        {},
        ["history"],
      );
    if (action.type === "redo" && !game.redo.length)
      return domainFailure(
        "NOTHING_TO_REDO",
        "There is no board edit to redo.",
        {},
        ["history"],
      );
    if (
      ["advance_hint", "dismiss_hint", "apply_hint"].includes(action.type) &&
      !game.hint
    )
      return domainFailure(
        "NO_ACTIVE_HINT",
        "Request a hint first.",
        {},
        ["hint"],
        ["request_hint"],
      );
    if (
      action.type === "advance_hint" &&
      game.hint_stage >= game.hint.stages.length - 1
    )
      return domainFailure(
        "HINT_ALREADY_FINAL",
        "The hint is ready to apply.",
        {},
        ["hint"],
        ["apply_hint"],
      );
    if (
      action.type === "apply_hint" &&
      game.hint_stage < game.hint.stages.length - 1
    )
      return domainFailure(
        "HINT_NOT_READY",
        "Advance through every visible hint stage before applying it.",
        { current: game.hint_stage + 1, required: game.hint.stages.length },
        ["hint"],
        ["advance_hint"],
      );
    return { ok: true };
  }

  updateGame(game) {
    this.store.dispatch({ type: "UPDATE_GAME", game });
  }

  applyPublicProgress(game) {
    const puzzle = this.puzzle();
    const events = [];
    const filled = [...game.values].filter((value) => value !== "0").length;
    const givenCount = [...puzzle.givens].filter(
      (value) => value !== "0",
    ).length;
    const occupancy = (filled - givenCount) / (81 - givenCount);
    for (const threshold of [0.25, 0.5, 0.75]) {
      if (
        occupancy >= threshold &&
        !game.occupancy_milestones.includes(threshold)
      ) {
        game = {
          ...game,
          occupancy_milestones: [...game.occupancy_milestones, threshold],
        };
        events.push(
          event("milestone", `progress_${threshold * 100}`, {
            progress: threshold,
          }),
        );
      }
    }
    const units = publicUnitCompletions(game);
    if (units.length) {
      game = {
        ...game,
        completed_units: [
          ...game.completed_units,
          ...units.map((item) => item.key),
        ],
      };
      events.push(
        ...units.map((item) =>
          event("milestone", item.event, { unit: item.key }),
        ),
      );
    }
    this.updateGame(game);
    return { game, events };
  }

  completeIfNeeded(game) {
    const puzzle = this.puzzle();
    if (!isComplete(game, puzzle) || game.completed_at)
      return { game, events: [] };
    const completedAt = this.now().toISOString();
    game = {
      ...game,
      completed_at: completedAt,
      timer_running: false,
      paused: false,
      reveal_progress: 1,
      companion_state: this.director?.snapshot() ?? game.companion_state,
    };
    const record = completionRecord(game, puzzle);
    this.store.dispatch({ type: "COMPLETE_GAME", game, record });
    this.onComplete(record, game);
    return {
      game,
      events: [
        event("victory", "puzzle_completed", {
          puzzle_id: puzzle.id,
          collection_item_id: puzzle.collection_item_id,
        }),
      ],
    };
  }

  recordEvents(items) {
    if (!items.length) return;
    const stamped = items.map((item) => ({
      ...item,
      sequence: this.acceptedSequence,
    }));
    this.events = [...this.events, ...stamped].slice(-200);
    this.onEvents(stamped);
  }

  companionEvents(items) {
    if (!this.director) return [];
    const mappings = [];
    for (const item of items) {
      if (item.type === "puzzle_started")
        mappings.push(item.data.daily_date ? "daily_start" : "game_start");
      else if (item.type === "puzzle_resumed" || item.type === "game_resumed")
        mappings.push("session_resume");
      else if (["conflict_introduced", "mistake_recorded"].includes(item.type))
        mappings.push("conflict_visible");
      else if (item.type === "hint_opened")
        mappings.push(`hint_${item.data.technique}`);
      else if (item.type === "game_paused") mappings.push("game_pause");
      else if (item.type === "cell_revealed") mappings.push("reveal_cell");
      else if (item.type === "puzzle_completed")
        mappings.push("completion", "collection_unlock");
      else if (
        [
          "correction",
          "undo",
          "redo",
          "notes_on",
          "notes_off",
          "progress_25",
          "progress_50",
          "progress_75",
          "row_complete",
          "column_complete",
          "box_complete",
        ].includes(item.type)
      )
        mappings.push(item.type);
    }
    const output = [];
    for (const name of mappings) {
      const forced = [
        "completion",
        "collection_unlock",
        "reveal_cell",
      ].includes(name);
      const line = this.director.line(
        name,
        {
          action_count: this.state.activeGame?.action_count ?? 0,
          frequency: this.state.settings.companionFrequency,
        },
        { force: forced, priority: forced ? 100 : 0 },
      );
      if (!line) continue;
      this.store.dispatch({ type: "COMPANION_LINE", line });
      output.push(event("important_result", "companion_line", { line }));
    }
    return output;
  }

  snapshotMeta() {
    return {
      accepted_sequence: this.acceptedSequence,
      revision: this.revision,
      events: structuredClone(this.events),
    };
  }
}

export function completionRecord(game, puzzle) {
  return {
    id: `${puzzle.id}:${game.completed_at}`,
    puzzle_id: puzzle.id,
    collection_item_id: puzzle.collection_item_id,
    difficulty: puzzle.difficulty.label,
    elapsed_active_ms: Math.round(game.elapsed_active_ms),
    mistakes: game.mistakes,
    hints_used: game.hints_used,
    reveals_used: game.reveals_used,
    notes_used: game.history.some((command) => command.type === "note"),
    started_at: game.started_at,
    completed_at: game.completed_at,
    daily_date: game.daily_date,
  };
}

function event(className, type, data = {}) {
  return { class: className, type, data };
}
function domainFailure(
  code,
  message,
  evidence = {},
  observations = [],
  actions = [],
) {
  return {
    ok: false,
    error: { code, message, evidence, recovery: { observations, actions } },
  };
}
function summarizeChanges(before, after) {
  if (!before && after) return { puzzle_id: after.puzzle_id, started: true };
  if (!before || !after) return {};
  const changedCells = [];
  for (let cell = 0; cell < 81; cell += 1)
    if (
      before.values[cell] !== after.values[cell] ||
      before.notes[cell] !== after.notes[cell]
    )
      changedCells.push(`r${Math.floor(cell / 9) + 1}c${(cell % 9) + 1}`);
  return {
    changed_cells: changedCells.slice(0, 81),
    elapsed_active_ms: after.elapsed_active_ms,
    completed: Boolean(after.completed_at),
  };
}
